/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useRef } from 'react';
import { Button } from 'reactstrap';
import { FaMicrophoneAlt } from "react-icons/fa";
import { Tooltip, useTheme } from '@mui/material';


const AudioRecorder = ({ handleAudio, chatInput, setChatInput, isMobile=false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const chunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const theme = useTheme();

  const startRecording = () => {
    // Clear the chunks array at the start of each new recording
    chunksRef.current = [];
  
    // Check if the browser supports the required APIs
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder) {
      // Request access to the microphone
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          mediaRecorderRef.current = new MediaRecorder(stream);
  
          // When data is available, add it to our chunks
          mediaRecorderRef.current.ondataavailable = event => {
            chunksRef.current.push(event.data);
          };
  
          // When the recording stops, create a Blob and a URL to it
          mediaRecorderRef.current.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: 'audio/mp4' });
            const url = URL.createObjectURL(blob);
  
            // Set the source of the audio element to the URL of the blob
            if (audioRef.current) {
              audioRef.current.src = url;
            }
          };
  
          // Start recording
          mediaRecorderRef.current.start();
          setIsRecording(true);
        })
        .catch(error => {
          console.error('Error accessing the microphone', error);
        });
    } else {
      console.error('Your browser does not support the required APIs');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      // When the recording stops, create a Blob and stop all tracks
      mediaRecorderRef.current.onstop = () => {
        // Create a Blob from the recorded data
        const blob = new Blob(chunksRef.current, { type: 'audio/mp4' });
        handleAudio(blob, chatInput, setChatInput);
  
        // Clear the chunks array after creating the Blob
        chunksRef.current = [];
  
        // Stop all tracks in the MediaStream
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      };
  
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
        
  return (
    <Tooltip
      title={'Record'}
      placement="top"
      arrow
      enterDelay={500}
      leaveDelay={200}
    >
      <div>
        {!isRecording && <Button aria-label={'start recording'} onClick={startRecording} className="chat-log-file-btn"><FaMicrophoneAlt size={isMobile ? 18 : 20} color={theme.palette.text.primary} alt="Record" /></Button>}
        {isRecording && <Button aria-label={'stop recording'} onClick={stopRecording} className="chat-log-file-btn red-background"><img src="stop.png" style={{width: '20px'}} alt="Stop" /></Button>}
      </div>
    </Tooltip>
  );
};

export default AudioRecorder;
